import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getSalesByClient } from "@/Api/sales";
import { getSalesByClient as getSalesClient } from "@/Api/clients";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { getSelectUnities } from "@/Api/unities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { formatDate } from "@/utils/format-date";
import { formatCurrency } from "@/utils/format-currency";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { ChevronDown } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { useAuth } from "@/hooks/auth";

interface Product {
  id: number;
  product_name: string;
  pivot: {
    quantity: number;
    price_unit: string;
    price_total: string;
  };
  product_classification: {
    name: string;
  };
  contest: {
    name: string;
    lottery: {
      name: string;
      color: string;
    };
  };
}

interface ClientPurchase {
  id: number;
  uuid: string;
  created_at: string;
  total: string;
  status?: string;
  unity: {
    id: number;
    name: string;
  };
  client: {
    uuid: string;
    id: number;
    name: string;
    email: string;
    balances: any;
    phone: string;
    total_balance?: number;
  };
  sale_type: {
    id: number;
    name: string;
  };
  payment_forms: Array<{
    id: number;
    value: string;
    payment_form: {
      id: number;
      name: string;
    };
  }>;
  process_step_sales: Array<{
    id: number;
    sale_step: {
      id: number;
      name: string;
    };
    process_status: {
      id: number;
      name: string;
    };
  }>;
  seller: {
    id: number;
    user: {
      id: number;
      name: string;
      email: string;
    };
  };
  products: Product[];
  transaction?: {
    id: number;
    status: string;
    amount: string;
  } | null;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export const ClientPurchases = () => {
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState<ClientPurchase[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });
  const [filters, setFilters] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    uuid: "",
    page: 1,
    start_date: "",
    end_date: "",
    sale_type_id: "all",
    exclude_cancelled: true,
    unity_id: "all",
    min_total: "",
    max_total: "",
  });
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [clientSales, setClientSales] = useState<ClientPurchase[]>([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [saleTypes, setSaleTypes] = useState<{ id: number; name: string }[]>([]);
  const [unities, setUnities] = useState<{ id: number; name: string }[]>([]);
  const { hasPermission } = usePermissions();
  const {user} = useAuth();
  const sellerId = user?.seller?.id;

  useEffect(() => {
    const fetchSelectOptions = async () => {
      try {
        const [saleTypesRes, unitiesRes] = await Promise.all([
          getSelectSaleTypes(),
          getSelectUnities(),
        ]);
        if (saleTypesRes.ok && saleTypesRes.data?.response) {
          setSaleTypes(saleTypesRes.data.response);
        }
        if (unitiesRes.ok && unitiesRes.data?.response) {
          setUnities(unitiesRes.data.response);
        }
      } catch (error) {
        console.error("Error fetching select options:", error);
      }
    };
    fetchSelectOptions();
  }, []);

  const handleSearch = async (page = 1) => {
    try {
      setLoading(true);
      const { data, ok } = await getSalesByClient({
        client_name: filters.client_name || undefined,
        client_phone: filters.client_phone || undefined,
        client_email: filters.client_email || undefined,
        page,
        seller_id: sellerId,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        sale_type_id: filters.sale_type_id !== "all" ? filters.sale_type_id : undefined,
        exclude_cancelled: filters.exclude_cancelled,
        unity_id: filters.unity_id !== "all" ? filters.unity_id : undefined,
        min_total: filters.min_total || undefined,
        max_total: filters.max_total || undefined,
      });

      if (ok && data?.response) {
        setPurchases(data.response.data || []);
        setPaginationData({
          current_page: data.response.current_page || 1,
          last_page: data.response.last_page || 1,
          per_page: data.response.per_page || 15,
          total: data.response.total || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching client purchases:", error);
      toast.error("Erro ao buscar compras do cliente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
    handleSearch(page);
  };

  const handlePreviousPage = () => {
    if (paginationData.current_page > 1) {
      handlePageChange(paginationData.current_page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationData.current_page < paginationData.last_page) {
      handlePageChange(paginationData.current_page + 1);
    }
  };

  const handleLastPage = () => {
    handlePageChange(paginationData.last_page);
  };

  const handleWhatsAppClick = (phone: string) => {
    if (!phone) return;
    const formattedPhone = phone.replace(/\D/g, "");
    window.open(`https://wa.me/55${formattedPhone}`, "_blank");
  };

  const getLastPrizeWon = (purchase: ClientPurchase) => {
    if (purchase?.client?.balances && purchase?.client?.balances?.length > 0) {
      const lastBalance = purchase?.client?.balances?.[0];
      const formattedBalance = Mask.formatCurrency(lastBalance.balance);
      const createdDate = new Date(lastBalance.created_at).toLocaleDateString(
        "pt-BR"
      );
      return `${formattedBalance} (${createdDate})`;
    }
    return "Nenhum prêmio";
  };

  const toggleExpandClient = async (clientUuid: string) => {
    if (expandedClient === clientUuid) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientUuid);
      setLoadingSales(true);
      try {
        const { data, ok } = await getSalesClient(clientUuid, {
          seller_id: sellerId,
        });
        if (ok) {
          setClientSales(data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching client sales:", error);
        toast.error("Erro ao buscar vendas do cliente");
      } finally {
        setLoadingSales(false);
      }
    }
  };

  return (
    <Main>
      <If condition={hasPermission("view-purchases-by-client-list")}>
        <div className="space-y-4 p-4">
          <Card className="p-4">
            <h2 className="text-2xl font-bold mb-4">Compras por Cliente</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Nome do cliente</Label>
                <Input
                  placeholder="Nome do cliente"
                  value={filters.client_name}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      client_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  placeholder="Telefone"
                  value={filters.client_phone}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      client_phone: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input
                  placeholder="E-mail"
                  value={filters.client_email}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      client_email: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Venda</Label>
                <Select
                  value={filters.sale_type_id}
                  onValueChange={(value) =>
                    setFilters((prev) => ({ ...prev, sale_type_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {saleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidade</Label>
                <Select
                  value={filters.unity_id}
                  onValueChange={(value) =>
                    setFilters((prev) => ({ ...prev, unity_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {unities.map((unity) => (
                      <SelectItem key={unity.id} value={unity.id.toString()}>
                        {unity.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data Inicial</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      start_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Data Final</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      end_date: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Valor Mínimo</Label>
                <Input
                  type="number"
                  placeholder="R$ 0,00"
                  value={filters.min_total}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      min_total: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Valor Máximo</Label>
                <Input
                  type="number"
                  placeholder="R$ 0,00"
                  value={filters.max_total}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      max_total: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="exclude-cancelled"
                  checked={filters.exclude_cancelled}
                  onCheckedChange={(checked) =>
                    setFilters((prev) => ({
                      ...prev,
                      exclude_cancelled: checked,
                    }))
                  }
                />
                <Label htmlFor="exclude-cancelled" className="cursor-pointer">
                  Excluir vendas canceladas
                </Label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setFilters(prev => ({ ...prev, page: 1 }));
                  handleSearch(1);
                }}
                disabled={loading}
                className={cn(colors.bg_primary)}
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setFilters({
                    client_name: "",
                    client_phone: "",
                    client_email: "",
                    uuid: "",
                    page: 1,
                    start_date: "",
                    end_date: "",
                    sale_type_id: "all",
                    exclude_cancelled: true,
                    unity_id: "all",
                    min_total: "",
                    max_total: "",
                  });
                }}
                disabled={loading}
              >
                Limpar Filtros
              </Button>
            </div>
          </Card>

          <Card className="p-4">
            <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Última Compra</TableHead>
                  <TableHead>Vendedor</TableHead>
                  <TableHead>Crédito Cliente</TableHead>
                  <TableHead>Último Prêmio Ganho</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <>
                    <TableRow key={purchase.uuid}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {purchase?.client?.name}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {purchase?.client?.email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{formatDate(purchase.created_at)}</span>
                          <span className="text-sm font-medium">
                            {formatCurrency(Number(purchase.total))}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {purchase?.seller?.user?.name || "-"}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(purchase?.client?.total_balance || 0)}
                      </TableCell>
                      <TableCell>{getLastPrizeWon(purchase)}</TableCell>
                      <TableCell>
                        {Mask.maskPhone(purchase?.client?.phone)}

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 ml-2 p-0 text-green-600 hover:text-green-800 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900"
                          onClick={() =>
                            handleWhatsAppClick(purchase?.client?.phone)
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                          </svg>
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-3 w-full justify-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900",
                              expandedClient === purchase?.client?.uuid &&
                                "bg-blue-100 dark:bg-blue-900"
                            )}
                            onClick={() =>
                              toggleExpandClient(purchase?.client?.uuid)
                            }
                          >
                            <ChevronDown
                              size={16}
                              className={cn(
                                "transition-transform",
                                expandedClient === purchase?.client?.uuid &&
                                  "transform rotate-180"
                              )}
                            />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedClient === purchase?.client?.uuid && (
                      <TableRow className="bg-gray-50 dark:bg-[#353535]">
                        <TableCell colSpan={7} className="p-0">
                          <div className="p-4">
                            {loadingSales ? (
                              <div className="py-4 text-center">
                                Carregando vendas...
                              </div>
                            ) : clientSales.length > 0 ? (
                              <Table className="border rounded">
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Valor</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Forma de Pagamento</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Produtos</TableHead>
                                    <TableHead>Vendedor</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {clientSales.map((sale) => (
                                    <TableRow key={sale.uuid}>
                                      <TableCell>
                                        {formatDate(sale.created_at)}
                                      </TableCell>
                                      <TableCell>
                                        {formatCurrency(Number(sale.total))}
                                      </TableCell>
                                      <TableCell>
                                        {sale.sale_type.name}
                                      </TableCell>
                                      <TableCell>
                                        {sale.payment_forms.map((form) => (
                                          <div key={form.id}>
                                            {form.payment_form.name}:{" "}
                                            {formatCurrency(Number(form.value))}
                                          </div>
                                        ))}
                                      </TableCell>
                                      <TableCell>
                                        {sale.process_step_sales.sort(
                                          (a, b) => b.id - a.id
                                        )[0]?.sale_step.name || "N/A"}
                                      </TableCell>
                                      <TableCell>
                                        <div className="max-h-24 overflow-y-auto">
                                          {sale.products.map((product, idx) => (
                                            <div
                                              key={idx}
                                              className="mb-2 border-b pb-1"
                                            >
                                              <div className="font-medium">
                                                {product.product_name}
                                              </div>
                                              <div className="text-xs">
                                                Qtd: {product.pivot.quantity} x{" "}
                                                {formatCurrency(
                                                  Number(
                                                    product.pivot.price_unit
                                                  )
                                                )}
                                              </div>
                                              <div
                                                className="mt-1 px-2 py-1 rounded-md text-white text-xs inline-block"
                                                style={{
                                                  backgroundColor:
                                                    product?.contest?.lottery
                                                      ?.color,
                                                }}
                                              >
                                                {
                                                  product?.contest?.lottery
                                                    ?.name
                                                }{" "}
                                                - {product?.contest?.name}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        {sale?.seller?.user?.name || "-"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <div className="py-4 text-center">
                                Nenhuma venda encontrada para este cliente
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {purchases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-4">
                      Nenhuma compra encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
              {purchases.length > 0 && (
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Página {paginationData.current_page} de {paginationData.last_page} 
                  ({paginationData.total} registros)
                </span>
              )}
              {paginationData.last_page > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        className={cn("h-8", colors.bg_primary)}
                        onClick={(e) => {
                          e.preventDefault();
                          handlePreviousPage();
                        }}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        className={cn(
                          `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                        )}
                      >
                        <span className="pointer-events-none dark:text-[#F4F4F5]">
                          {paginationData.current_page}
                        </span>
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        className="text-black h-8 hover:bg-stone-950/10"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLastPage();
                        }}
                      >
                        <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        className={cn("h-8", colors.bg_primary)}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNextPage();
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </Card>
        </div>
      </If>
    </Main>
  );
};
